import React, { useState, useEffect } from 'react';
import { Mail, MessageSquare, Building2, HelpCircle } from 'lucide-react';
import { Modal } from './ui/Modal';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { useAuthStore } from '../store/authStore';
import contactService from '../services/contactService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TYPE_OPTIONS = [
  { value: 'enterprise', label: 'Enterprise inquiry' },
  { value: 'support',    label: 'Support request'   },
  { value: 'general',    label: 'General feedback'  },
];

const TYPE_META = {
  enterprise: {
    title:    'Enterprise Inquiry',
    subtitle: 'Tell us about your needs and we\'ll get back to you shortly.',
    icon:     <Building2 size={24} className="text-amber-500" />,
  },
  support: {
    title:    'Support Request',
    subtitle: 'Describe your issue and our team will help you out.',
    icon:     <HelpCircle size={24} className="text-blue-500" />,
  },
  general: {
    title:    'Contact Us',
    subtitle: 'Have a question or feedback? We\'d love to hear from you.',
    icon:     <MessageSquare size={24} className="text-blue-500" />,
  },
};

const labelStyle = {
  display: 'block',
  marginBottom: '0.375rem',
  fontSize: '0.6875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#6b7280',
};

export default function ContactFormModal({ isOpen, onClose, type: typeProp = 'general', lockType = false, showToast }) {
  const { user } = useAuthStore();
  const hasProfile = !!user;

  const profileName  = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.name || '' : '';
  const profileEmail = user?.email || '';

  const [useProfile, setUseProfile] = useState(true);
  const [name,       setName]       = useState('');
  const [email,      setEmail]      = useState('');
  const [type,       setType]       = useState(typeProp);
  const [subject,    setSubject]    = useState('');
  const [message,    setMessage]    = useState('');
  const [sending,    setSending]    = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setUseProfile(hasProfile);
    setName(profileName);
    setEmail(profileEmail);
    setType(typeProp);
    setSubject('');
    setMessage('');
    setSending(false);
    setError('');
  }, [isOpen, typeProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync name/email when toggling useProfile
  useEffect(() => {
    if (!isOpen) return;
    if (useProfile) {
      setName(profileName);
      setEmail(profileEmail);
    }
  }, [useProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeName  = useProfile ? profileName  : name;
  const activeEmail = useProfile ? profileEmail : email;

  const meta = TYPE_META[type] || TYPE_META.general;

  const canSubmit =
    activeName.trim() &&
    EMAIL_RE.test(activeEmail.trim()) &&
    subject.trim() &&
    message.trim() &&
    !sending;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSending(true);
    setError('');
    try {
      await contactService.submit({
        name:    activeName.trim(),
        email:   activeEmail.trim(),
        type,
        subject: subject.trim(),
        message: message.trim(),
      });
      showToast?.('Your message has been sent. We\'ll get back to you soon.', 'success');
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={meta.title}
      subtitle={meta.subtitle}
      customIcon={meta.icon}
      size="lg"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

        {error && (
          <div style={{
            borderRadius: '0.5rem',
            border: '1px solid #fecaca',
            background: '#fef2f2',
            padding: '0.5rem 0.75rem',
            fontSize: '0.8125rem',
            color: '#b91c1c',
          }}>
            {error}
          </div>
        )}

        {/* Profile toggle — only shown when logged in */}
        {hasProfile && (
          <div className="settings-row border-b border-gray-100 dark:border-zinc-700" style={{ paddingBottom: '0.25rem' }}>
            <div>
              <p className="settings-row-label">Use my profile details</p>
              <p className="settings-row-desc">{profileName} · {profileEmail}</p>
            </div>
            <button
              type="button"
              className={`settings-toggle flex-shrink-0${useProfile ? ' settings-toggle--on' : ''}`}
              onClick={() => setUseProfile(v => !v)}
              aria-checked={useProfile}
              role="switch"
            >
              <span className="settings-toggle-thumb" />
            </button>
          </div>
        )}

        {/* Name + Email — locked when useProfile is on */}
        {(!hasProfile || !useProfile) && (
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your name"
                className="modal-input"
                required
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Email</label>
              <div style={{ position: 'relative' }}>
                <Mail size={14} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="modal-input"
                  style={{ paddingLeft: '2.125rem' }}
                  required
                />
              </div>
            </div>
          </div>
        )}

        {!lockType && (
          <div>
            <label style={labelStyle}>Inquiry type</label>
            <div className="modal-picker">
              <CompactSelectPicker
                value={type}
                onChange={setType}
                options={TYPE_OPTIONS}
                aria-label="Inquiry type"
              />
            </div>
          </div>
        )}

        <div>
          <label style={labelStyle}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder={
              type === 'enterprise' ? 'e.g. Enterprise pricing for 200 seats'
              : type === 'support'  ? 'e.g. Unable to import collection'
              : 'What\'s this about?'
            }
            className="modal-input"
            required
          />
        </div>

        <div>
          <label style={labelStyle}>Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={
              type === 'enterprise' ? 'Tell us about your team size, security requirements, and any custom needs…'
              : type === 'support'  ? 'Describe the issue in detail — steps to reproduce help a lot.'
              : 'Share your thoughts…'
            }
            rows={5}
            className="modal-form-textarea"
            required
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.25rem' }}>
          <button
            type="button"
            onClick={onClose}
            className="modal-btn-cancel"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-lg bg-blue-600 text-white modern-button"
            style={{
              padding: '0.5rem 1.25rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              border: 'none',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.5,
            }}
          >
            {sending ? 'Sending…' : 'Send message'}
          </button>
        </div>

      </form>
    </Modal>
  );
}
